import {
  Box,
  Card,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Stack,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Category as CategoryIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

function CategoriesList({
  sortedCategoryEntries,
  selectedCategoryId,
  onSelectCategory,
  onAddCategory,
  onDeleteCategory,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Card
      elevation={3}
      sx={{
        width: "35%",
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 2.5,
          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 80,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <CategoryIcon sx={{ color: "success.main", fontSize: 28 }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              color="text.primary"
              sx={{ lineHeight: 1.2 }}
            >
              {t("precautions.categoriesTitle")}
            </Typography>
          </Stack>
          <IconButton
            color="success"
            onClick={onAddCategory}
            sx={{
              bgcolor: alpha(theme.palette.success.main, 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.success.main, 0.2),
                transform: "scale(1.05)",
              },
              transition: "all 0.3s ease",
            }}
            aria-label={t("precautions.addCategoryButtonAria")}
          >
            <AddIcon />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto" }}>
        {sortedCategoryEntries.length > 0 ? (
          <List sx={{ p: 2 }}>
            {sortedCategoryEntries.map(([categoryId, group]) => (
              <ListItemButton
                key={categoryId}
                selected={selectedCategoryId === categoryId}
                onClick={() => onSelectCategory(categoryId)}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor:
                    selectedCategoryId === categoryId
                      ? "success.main"
                      : "divider",
                  transition: "all 0.3s ease",
                  background:
                    selectedCategoryId === categoryId
                      ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
                      : "transparent",
                  "&:hover": {
                    borderColor: "success.main",
                    boxShadow: theme.shadows[2],
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                  },
                  "&.Mui-selected": {
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                    "&:hover": {
                      background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                    },
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor:
                        selectedCategoryId === categoryId
                          ? alpha(theme.palette.success.main, 0.2)
                          : alpha(theme.palette.success.main, 0.1),
                      border: "2px solid",
                      borderColor:
                        selectedCategoryId === categoryId
                          ? alpha(theme.palette.success.main, 0.4)
                          : alpha(theme.palette.success.main, 0.2),
                      width: 40,
                      height: 40,
                    }}
                  >
                    <CategoryIcon
                      sx={{
                        color: "success.main",
                        fontSize: 20,
                      }}
                    />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      fontWeight={selectedCategoryId === categoryId ? 700 : 500}
                      color="text.primary"
                    >
                      {group.category?.name || t("precautions.uncategorized")}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {group.precautions.length} {t("precautions.items")}
                    </Typography>
                  }
                />
                {group.precautions.length === 0 && (
                  <Tooltip title={t("precautions.deleteCategoryTooltip")}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteCategory(group.category);
                      }}
                      sx={{
                        opacity: 0.7,
                        "&:hover": {
                          opacity: 1,
                          bgcolor: alpha(theme.palette.error.main, 0.1),
                          transform: "scale(1.1)",
                        },
                        transition: "all 0.3s ease",
                      }}
                      aria-label={t("precautions.deleteCategoryButtonAria", {
                        name: group.category?.name,
                      })}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
            <Box
              sx={{
                display: "inline-flex",
                p: 3,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.success.main, 0.1),
                mb: 2,
              }}
            >
              <CategoryIcon
                sx={{
                  fontSize: 40,
                  color: "success.main",
                  opacity: 0.7,
                }}
              />
            </Box>
            <Typography variant="body1" color="text.secondary" fontWeight="500">
              {t("precautions.noCategories")}
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
}

export default CategoriesList;
